import { CircleCloseIcon } from 'components/Icons/CircleCloseIcon'
import { InputCurrency } from 'components/InputCurrency'
import { InputNumber } from 'components/InputNumber'
import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import {
  IPreferredSeatsDeckPriceSettingsProps,
  IPreferredSeatsDeckPriceValue
} from 'features/Ancillaries/components/PreferredSeatsDeckPriceSettings/types'
import { isEmpty } from 'lodash'
import { ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const Container = styled.div`
  border: 1px solid #eaeaea;
  border-radius: 8px;
  padding: 24px;
`
const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h4`
  margin: 0;
  color: #28265d;
  font-family: Roboto;
  font-size: 24px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const Item = styled.li``

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`
const DeleteButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`
const InputContainer = styled.div`
  width: 46px;
  flex-shrink: 0;
`
const StyledInput = styled(InputNumber)`
  & input {
    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
`
const PriceLabel = styled.span`
  white-space: nowrap;
`
const CloseIcon = styled(CircleCloseIcon)`
  fill: #b0b0b0;
`
export const PreferredSeatsDeckPriceSettings = ({
  title,
  value,
  onChange,
  touched,
  errors,
  currencyCode
}: IPreferredSeatsDeckPriceSettingsProps): ReactElement => {
  const priceRanges = value

  const handleAdd = (): void => {
    const newValue = [...priceRanges, { fromLabel: '', toLabel: '', price: '' }]
    onChange(newValue)
  }
  const handleDelete = (index: number): void => {
    const newValue = [...priceRanges]
    newValue.splice(index, 1)
    onChange(newValue)
  }
  const handleInputChange = (key: keyof IPreferredSeatsDeckPriceValue, index: number, value: string): void => {
    const newValue = [...priceRanges]
    newValue[index][key] = value
    onChange(newValue)
  }
  return (
    <Container>
      <Wrap>
        <Head>
          <Title>{title}</Title>
          <TopContainer>
            <FormattedMessage id="preferredSeatsDeckPriceSettings.new.seats" defaultMessage="Seats" />
            <Button type="button" kind="secondary" onClick={handleAdd}>
              <FormattedMessage id="preferredSeatsDeckPriceSettings.add" defaultMessage="Add" />
            </Button>
          </TopContainer>
        </Head>
        {value.length > 0 ? (
          <List>
            {priceRanges.map((_, index) => {
              return (
                <Item key={index}>
                  <ItemContent>
                    <InputContainer>
                      <StyledInput
                        maskOptions={{
                          max: 100,
                          min: 1
                        }}
                        type="number"
                        value={value[index].fromLabel}
                        onChange={(value: string): void => handleInputChange('fromLabel', index, value)}
                      />
                    </InputContainer>
                    <span>
                      <FormattedMessage id="preferredSeatsDeckPriceSettings.to" defaultMessage="to" />
                    </span>
                    <InputContainer>
                      <StyledInput
                        maskOptions={{
                          max: 100,
                          min: 1
                        }}
                        type="number"
                        value={value[index].toLabel}
                        onChange={(value: string): void => handleInputChange('toLabel', index, value)}
                      />
                    </InputContainer>
                    <PriceLabel>
                      <FormattedMessage
                        id="preferredSeatsDeckPriceSettings.constraints.price.label"
                        defaultMessage="Price per ticket"
                      />
                    </PriceLabel>
                    <InputCurrency
                      value={value[index].price}
                      placeholder="$0.00"
                      suffix={currencyCode}
                      hideSuffix={isEmpty(currencyCode)}
                      onChange={(value): void => {
                        handleInputChange('price', index, value)
                      }}
                    />

                    <DeleteButton
                      onClick={(): void => {
                        handleDelete(index)
                      }}
                      type="button"
                    >
                      <CloseIcon />
                    </DeleteButton>
                  </ItemContent>
                  <ErrorMessage>
                    {touched && errors && errors[index] ? errors[index]?.toLabel || errors[index]?.price : null}
                  </ErrorMessage>
                </Item>
              )
            })}
          </List>
        ) : null}
        {touched && typeof errors === 'string' ? <ErrorMessage>{errors}</ErrorMessage> : null}
      </Wrap>
    </Container>
  )
}
